import { useState, useEffect } from 'react';
import { Users, Eye, Settings } from 'lucide-react';
import { NewManageTeamPage } from './NewManageTeamPage';
import { ViewDetailsModal } from './ViewDetailsModal';
import DashboardLayout from '../../layouts/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { apiGetUserTeams } from '../../api/auth';

interface Team {
  id: string;
  name: string;
  hackathon: string;
  hackathonId: string;
  maxTeamSize: number;
  status: 'Selected' | 'Under Review' | 'Registered' | 'Rejected';
  leader: { name: string; email: string; avatar: string };
  members: { name: string; email?: string; grade?: string; jobRole?: string; avatar: string }[];
}

const statusStyles = {
  'Selected': 'bg-[#00d5d0] text-white',
  'Under Review': 'bg-[#feb100] text-white',
  'Registered': 'bg-[#1a73e8] text-white',
  'Rejected': 'bg-[#e4e8ee] text-[#6e7891]',
};

export default function AllMyTeamsPage() {
  const { user, token } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'All Teams' | 'Selected' | 'Under Review' | 'Registered' | 'Rejected'>('All Teams');
  const [selectedTeam, setSelectedTeam] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [detailsTeam, setDetailsTeam] = useState<any>(null);

  useEffect(() => {
    const fetchTeams = async () => {
      if (!token) return;
      try {
        const data = await apiGetUserTeams(token);
        console.log('Fetched teams:', data.teams);
        setTeams(data.teams);
      } catch (err) {
        console.error('Failed to fetch teams:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, [token]);

  const statusCounts = {
    'All Teams': teams.length,
    'Selected': teams.filter(t => t.status === 'Selected').length,
    'Under Review': teams.filter(t => t.status === 'Under Review').length,
    'Registered': teams.filter(t => t.status === 'Registered').length,
    'Rejected': teams.filter(t => t.status === 'Rejected').length,
  };

  const filteredTeams = activeTab === 'All Teams' ? teams : teams.filter(t => t.status === activeTab);

  if (selectedTeam) {
    return (
      <NewManageTeamPage
        teamId={selectedTeam.id}
        teamName={selectedTeam.name}
        hackathonName={selectedTeam.hackathon}
        leaderName={selectedTeam.leader.name}
        leaderEmail={selectedTeam.leader.email}
        initialMembers={selectedTeam.members.filter((member: any) => member.email !== selectedTeam.leader.email).map((member: any, idx: number) => ({
          id: String(idx),
          name: member.name,
          email: `${member.email}`,
          grade: `${member.grade}`,
          jobRole: `${member.jobRole}`,
          status: 'Active' as const,
        }))}
        maxTeamSize={selectedTeam.maxTeamSize}
        onBack={() => setSelectedTeam(null)}
      />
    );
  }

  return (
    <DashboardLayout>
      <div className="bg-[#f9fafb] min-h-screen p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1a1f36] mb-2">My Teams</h1>
            <p className="text-[#6e7891] text-sm sm:text-base">View and manage all the teams you are part of across hackathons.</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500 text-lg">Loading teams...</p>
            </div>
          ) : (
            <div>
              {/* Tabs - Scrollable on Mobile */}
              <div className="mb-6 border-b border-[#e4e8ee]">
  <div className="flex gap-6 sm:gap-8 overflow-x-auto whitespace-nowrap scrollbar-hide">
    {Object.entries(statusCounts).map(([tab, count]) => (
      <button
        key={tab}
        onClick={() => setActiveTab(tab as any)}
        className={`pb-2 px-2 sm:px-4 text-sm font-medium flex-shrink-0 ${
          activeTab === tab
            ? 'text-[#0058ab] border-b-2 border-[#0058ab]'
            : 'text-[#6e7891]'
        }`}
      >
        {tab} ({count})
      </button>
    ))}
  </div>
</div>

              {/* Team Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredTeams.map((team) => (
                  <div
                    key={team.id}
                    className={`bg-white rounded-lg shadow-sm border-l-4 border-[#00d5d0] p-6`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-[#1a1f36]">{team.name}</h3>
                        <p className="text-[#0058ab] text-sm">{team.hackathon}</p>
                      </div>
                      <span className={`px-3 py-1 rounded text-xs font-medium ${statusStyles[team.status]}`}>
                        {team.status}
                      </span>
                    </div>

                    <div className="flex items-center mb-4">
                      <img src={team.leader.avatar} alt={team.leader.name} className="w-10 h-10 rounded-full mr-3" />
                      <div>
                        <p className="font-medium text-[#1a1f36]">{team.leader.name}</p>
                        <p className="text-xs text-[#00828e]">Team Leader</p>
                      </div>
                    </div>

                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 text-[#0058ab] mr-2" />
                        <span className="text-sm font-medium text-[#1a1f36]">{team.members.length} Members</span>
                      </div>
                      <div className="flex -space-x-2">
                        {team.members.slice(0, 4).map((member, idx) => (
                          <img key={idx} src={member.avatar} alt={member.name} className="w-8 h-8 rounded-full border-2 border-white" />
                        ))}
                      </div>
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setDetailsTeam(team);
                          setIsDetailsOpen(true);
                        }}
                        className="flex-1 bg-[#0058ab] text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-[#004080] transition-colors flex items-center justify-center"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </button>
                      <button
                        onClick={() => setSelectedTeam(team)}
                        className="flex-1 border border-[#0058ab] text-[#0058ab] py-2 px-4 rounded-lg text-sm font-medium hover:bg-[#00d5d0] hover:text-white transition-colors flex items-center justify-center"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Manage Team
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {detailsTeam && (
            <ViewDetailsModal
              isOpen={isDetailsOpen}
              onClose={() => {
                setIsDetailsOpen(false);
                setDetailsTeam(null);
              }}
              teamName={detailsTeam.name}
              hackathonName={detailsTeam.hackathon}
              leader={detailsTeam.leader}
              members={detailsTeam.members.map((member: any, idx: number) => ({
                id: String(idx),
                name: member.name,
                avatar: member.avatar,
                status: 'Active' as const,
              }))}
              status={detailsTeam.status}
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}